import { useEffect, useMemo, useRef, useState } from 'react';
import { elapsedSinceStart, finalRotation, indexUnderPointer, MAX_LABELLED_SEGMENTS, rotationAt, segmentAngle, segmentColor, segmentTextColor } from './wheel';
import { shortName } from './format';

export interface WheelSpin {
  winnerIndex: number;
  landingJitter: number;
  startedAtUtc: string;
  serverNowUtc: string;
  spinSeconds: number;
  /** performance/Date clock (ms) when the payload arrived on this client. */
  receivedAtMs: number;
}

interface WheelProps {
  names: string[];
  size?: number;
  /** When set, the wheel animates deterministically to spin.winnerIndex. */
  spin?: WheelSpin | null;
  /** Slow idle rotation for preview / ready screens. */
  idle?: boolean;
  logoSrc?: string;
  onSpinStart?: () => void;
  onSpinEnd?: () => void;
  onPointerName?: (name: string) => void;
  onRemaining?: (seconds: number) => void;
  className?: string;
}

function arcPath(cx: number, cy: number, r: number, a0: number, a1: number): string {
  // angles in degrees clockwise from top
  const rad = (a: number) => ((a - 90) * Math.PI) / 180;
  const x0 = cx + r * Math.cos(rad(a0)), y0 = cy + r * Math.sin(rad(a0));
  const x1 = cx + r * Math.cos(rad(a1)), y1 = cy + r * Math.sin(rad(a1));
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`;
}

/**
 * SVG roulette wheel. Never chooses a winner: with `spin` set it animates for exactly spinSeconds from the
 * server's StartedAtUtc and stops inside the server-selected segment. A display that reconnects mid-spin
 * resumes at the correct angle; one that reconnects after the end renders the final position immediately.
 */
export function Wheel({ names, size = 640, spin, idle, logoSrc, onSpinStart, onSpinEnd, onPointerName, onRemaining, className }: WheelProps) {
  const groupRef = useRef<SVGGElement>(null);
  const [lightPhase, setLightPhase] = useState(0);
  const n = Math.max(names.length, 1);
  const seg = segmentAngle(n);
  const c = 500, r = 440;
  const showLabels = names.length <= MAX_LABELLED_SEGMENTS;
  const fontSize = Math.max(14, Math.min(46, 900 / Math.max(n, 6)));
  const cb = useRef({ onSpinStart, onSpinEnd, onPointerName, onRemaining });
  cb.current = { onSpinStart, onSpinEnd, onPointerName, onRemaining };

  const segments = useMemo(() => names.map((name, i) => {
    const fill = segmentColor(i, names.length);
    const mid = (i + 0.5) * seg;
    return { name, fill, text: segmentTextColor(fill), path: arcPath(c, c, r, i * seg, (i + 1) * seg), mid };
  }), [names, seg]);

  // Rim lights chase
  useEffect(() => {
    const t = setInterval(() => setLightPhase((p) => (p + 1) % 2), spin ? 180 : 700);
    return () => clearInterval(t);
  }, [spin]);

  useEffect(() => {
    const g = groupRef.current;
    if (!g || names.length === 0) return;
    let raf = 0;
    const apply = (deg: number) => { g.style.transform = `rotate(${deg}deg)`; };

    if (spin) {
      const final = finalRotation(names.length, spin.winnerIndex, spin.landingJitter);
      let started = false, ended = false, lastIdx = -1, lastRemain = -1;
      const tick = () => {
        const elapsed = elapsedSinceStart(spin.startedAtUtc, spin.serverNowUtc, spin.receivedAtMs, Date.now());
        const rot = rotationAt(elapsed, spin.spinSeconds, final);
        apply(rot);
        if (!started) { started = true; cb.current.onSpinStart?.(); }
        const idx = indexUnderPointer(names.length, rot);
        if (idx !== lastIdx) { lastIdx = idx; cb.current.onPointerName?.(names[idx] ?? ''); }
        const remain = Math.max(0, spin.spinSeconds - elapsed / 1000);
        const rounded = Math.round(remain * 10) / 10;
        if (rounded !== lastRemain) { lastRemain = rounded; cb.current.onRemaining?.(rounded); }
        if (elapsed >= spin.spinSeconds * 1000) {
          if (!ended) { ended = true; cb.current.onSpinEnd?.(); }
          return;
        }
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    } else if (idle) {
      const t0 = performance.now();
      const tick = (t: number) => { apply(((t - t0) / 1000) * 12); raf = requestAnimationFrame(tick); };
      raf = requestAnimationFrame(tick);
    } else {
      apply(0);
    }
    return () => cancelAnimationFrame(raf);
  }, [spin, idle, names]);

  const lights = 28;
  return (
    <svg className={className} viewBox="0 0 1000 1000" width={size} height={size} role="img" aria-label={`Drawing wheel with ${names.length} entrants`}>
      <defs>
        <radialGradient id="kw-rim" cx="50%" cy="50%" r="50%">
          <stop offset="86%" stopColor="#0B2540" />
          <stop offset="93%" stopColor="#2F80C8" />
          <stop offset="100%" stopColor="#0B2540" />
        </radialGradient>
        <radialGradient id="kw-hub" cx="50%" cy="45%" r="60%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#DCE8F3" />
        </radialGradient>
        <filter id="kw-glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="12" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <circle cx={c} cy={c} r={496} fill="url(#kw-rim)" filter="url(#kw-glow)" />
      <g ref={groupRef} style={{ transformOrigin: '500px 500px', willChange: 'transform' }}>
        {segments.map((s, i) => (
          <g key={i}>
            <path d={s.path} fill={s.fill} stroke="#0B2540" strokeWidth={n > 120 ? 0.5 : 2} />
            {showLabels && (
              <text
                x={c} y={c - r * 0.6} fill={s.text} fontSize={fontSize} fontFamily="'Barlow Condensed', Barlow, sans-serif" fontWeight={700}
                textAnchor="middle" dominantBaseline="middle" transform={`rotate(${s.mid} ${c} ${c}) rotate(-90 ${c} ${c - r * 0.6})`}
              >
                {shortName(s.name).slice(0, 18)}
              </text>
            )}
          </g>
        ))}
        {names.length === 0 && <circle cx={c} cy={c} r={r} fill="#1F5F8F" />}
      </g>
      {Array.from({ length: lights }, (_, i) => {
        const a = ((i / lights) * 360 - 90) * (Math.PI / 180);
        const on = (i + lightPhase) % 2 === 0;
        return <circle key={i} cx={c + 468 * Math.cos(a)} cy={c + 468 * Math.sin(a)} r={9} fill={on ? '#FFE7A3' : '#5A6E82'} opacity={on ? 1 : 0.6} />;
      })}
      <circle cx={c} cy={c} r={128} fill="url(#kw-hub)" stroke="#0B2540" strokeWidth={8} />
      {logoSrc ? <image href={logoSrc} x={c - 100} y={c - 55} width={200} height={110} preserveAspectRatio="xMidYMid meet" /> : null}
      {/* pointer */}
      <path d="M 460 0 L 540 0 L 500 86 Z" fill="#F5B21B" stroke="#8A5A00" strokeWidth={4} filter="url(#kw-glow)" />
    </svg>
  );
}
